import CloudUpload from '@mui/icons-material/CloudUpload';
import Download from '@mui/icons-material/Download';
import Email from '@mui/icons-material/Email';
import { t } from 'i18next';
import { uniq } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import {
  BulkActionsBar as BulkActionsBarComponent,
  BulkActionsBarButton,
  BulkActionsBarCountButton,
} from '~components/BulkActionsBar/BulkActionsBar';
import OutboundEmailProgressContext from '~contexts/OutboundEmailProgressContext';
import { CompanyExportRenderer } from '~hooks/useCompanyExportRenderers/models';
import { OutboundEmailEntry } from '~hooks/useOutboundEmails/sendOutboundEmails';
import useSettlementOutboundEmails from '~hooks/useOutboundEmails/useSettlementOutboundEmails';
import useSettlementExportTracking from '~hooks/useSettlementExports/useSettlementExportTracking';
import useSettlementLineItems from '~hooks/useSettlementLineItems/useSettlementLineItems';
import { BulkSettlementExportResult, Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import EmailSettlementsDialog from '~pages/Approvals/DriverPay/Settlements/EmailSettlementsDialog';
import {
  canBulkExportToAccounting,
  canSendEmail,
  CLOSED_DOWNLOAD_DIALOG_STATE,
  DownloadDialogState,
  lineItemGroupsHaveTimesheetRateType,
  MAX_BULK_EXPORT_SETTLEMENTS,
} from '~pages/Approvals/DriverPay/Settlements/helper';
import DownloadFilesDialog from '~pages/Approvals/Files/components/DownloadFilesDialog/DownloadFilesDialog';
import useCompanyExportRenderersQuery from '~queries/useCompanyExportRenderersQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { SettlementSearchParamName } from '~store/SettlementsStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import {
  getSettlementExportRendererLabel,
  getSettlementExportSide,
  isSettlementAlreadyExported,
} from '~utils/settlements/settlement-export-utils';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import BulkExportSettlementsConfirmationDialog from './BulkExportSettlementsConfirmationDialog';
import BulkExportSettlementsFailedToast from './BulkExportSettlementsFailedToast';
import SettlementExportButton from './SettlementExportButton';
import SettlementReExportConfirmationDialog from './SettlementReExportConfirmationDialog';

interface SettlementsBulkActionsBarProps {
  isAccountingExportEnabled: boolean;
  selectedSettlementIds: string[];
  settlements: Settlement[];
  onBulkExportSubmittingChange: (isSubmitting: boolean) => void;
  onClearSelection: () => void;
  onDeselectSettlements: (settlementIds: string[]) => void;
  onVerifyAccountingConnection: () => Promise<boolean>;
}

const SettlementsBulkActionsBar = observer(
  ({
    isAccountingExportEnabled,
    selectedSettlementIds,
    settlements,
    onBulkExportSubmittingChange,
    onClearSelection,
    onDeselectSettlements,
    onVerifyAccountingConnection,
  }: SettlementsBulkActionsBarProps) => {
    const { settlementsStore, toasterStore, userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const { canEditSettlement } = userStore.getPermissions();
    const { getAllSettlementLineItems } = useSettlementLineItems();
    const { startSettlementExport } = useSettlementExportTracking();
    const { sendSettlementEmail } = useSettlementOutboundEmails();
    const { enqueueOutboundEmail } = useContext(OutboundEmailProgressContext);
    const { bulkExportSettlements, trackBulkExportSettlementsResult } = useSettlements();

    const category =
      settlementsStore.searchParams[SettlementSearchParamName.SETTLEMENT_CATEGORY];
    const settlementExportSide = getSettlementExportSide(category);
    const { companyExportRenderers } = useCompanyExportRenderersQuery({
      companyId: canEditSettlement ? companyId : null,
    });
    const companyExportRenderersForSide = companyExportRenderers.filter(
      (companyExportRenderer) => companyExportRenderer.side === settlementExportSide,
    );

    const [downloadDialogState, setDownloadDialogState] = useState<DownloadDialogState>(
      CLOSED_DOWNLOAD_DIALOG_STATE,
    );
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
    const [rendererPendingConfirmation, setRendererPendingConfirmation] =
      useState<CompanyExportRenderer | null>(null);
    const [isSubmittingExport, setIsSubmittingExport] = useState(false);
    // Two clicks in one tick both read the state from before React commits it, so
    // the ref is what actually blocks a second export request.
    const isSubmittingExportRef = useRef(false);
    const [isBulkExportDialogOpen, setIsBulkExportDialogOpen] = useState(false);
    // The request is in flight; the grid is blocked for exactly this long.
    const [isSubmittingBulkExport, setIsSubmittingBulkExport] = useState(false);
    // Accepted by the API, waiting for the results broadcast.
    const [isAwaitingBulkExportResult, setIsAwaitingBulkExportResult] = useState(false);
    const [isVerifyingAccountingConnection, setIsVerifyingAccountingConnection] =
      useState(false);
    // Same double-click guard as isSubmittingExportRef, held until the result arrives.
    const isBulkExportInFlightRef = useRef(false);

    const selectedSettlements = useMemo(() => {
      const loadedSettlementsById = new Map(
        settlements.map((settlement) => [settlement.id, settlement]),
      );

      return selectedSettlementIds.map((settlementId) =>
        loadedSettlementsById.get(settlementId),
      );
    }, [selectedSettlementIds, settlements]);
    // A selected id whose row isn't loaded (e.g. mid-refetch) can't be checked, so it
    // keeps the export hidden rather than offering an export the API may reject.
    const isSelectionExportable =
      settlementExportSide !== null &&
      selectedSettlements.every((settlement) =>
        settlement?.exportEligibleSides.includes(settlementExportSide),
      );
    // Like the export, an unloaded selected row can't be checked, so it disables the email.
    const isSelectionEmailable = selectedSettlements.every(
      (settlement) => settlement !== undefined && canSendEmail(settlement),
    );
    const alreadyExportedSettlementIds = useMemo(
      () =>
        selectedSettlements
          .filter(
            (settlement): settlement is Settlement =>
              settlement !== undefined && isSettlementAlreadyExported(settlement),
          )
          .map((settlement) => getFriendlySettlementId(settlement, category)),
      [category, selectedSettlements],
    );

    const exportableSettlementIds = useMemo(
      () =>
        selectedSettlementIds.filter((_, selectedIndex) => {
          const settlement = selectedSettlements[selectedIndex];

          return settlement !== undefined && canBulkExportToAccounting(settlement);
        }),
      [selectedSettlementIds, selectedSettlements],
    );
    const exportableSettlements = useMemo(
      () =>
        selectedSettlements.filter(
          (settlement): settlement is Settlement =>
            settlement !== undefined && canBulkExportToAccounting(settlement),
        ),
      [selectedSettlements],
    );
    const excludedFromBulkExportCount =
      selectedSettlementIds.length - exportableSettlementIds.length;

    const updateIsSubmittingBulkExport = useCallback(
      (isSubmitting: boolean) => {
        setIsSubmittingBulkExport(isSubmitting);
        onBulkExportSubmittingChange(isSubmitting);
      },
      [onBulkExportSubmittingChange],
    );

    const handleDownloadFilesClick = useCallback(async () => {
      setDownloadDialogState({ ...CLOSED_DOWNLOAD_DIALOG_STATE, isLoading: true });

      try {
        // Fetch line items for all selected settlements in parallel
        const lineItemsPromises = selectedSettlementIds.map((settlementId) =>
          getAllSettlementLineItems(companyId, settlementId),
        );

        const allLineItemGroups = await Promise.all(lineItemsPromises);

        // Extract unique job IDs from all line items
        const jobIds = uniq(
          allLineItemGroups.flatMap((lineItemGroups) =>
            lineItemGroups.flatMap((group) =>
              group.lineItems.flatMap((lineItem) => lineItem.ids.map((id) => id.jobId)),
            ),
          ),
        );

        if (jobIds.length === 0) {
          toasterStore.push(
            alert(
              t('approvals.driver_pay.settlements.bulk_no_jobs_for_download'),
              AlertTypes.info,
            ),
          );
          setDownloadDialogState(CLOSED_DOWNLOAD_DIALOG_STATE);
          return;
        }

        setDownloadDialogState({
          isOpen: true,
          jobIds,
          isLoading: false,
          preselectTimesheet: lineItemGroupsHaveTimesheetRateType(
            allLineItemGroups.flat(),
          ),
        });
      } catch (error) {
        setDownloadDialogState(CLOSED_DOWNLOAD_DIALOG_STATE);
        connection.handleRequestError(
          error,
          t('approvals.driver_pay.settlements.failed_to_fetch_jobs_for_download'),
        );
      }
    }, [companyId, selectedSettlementIds, getAllSettlementLineItems, toasterStore]);

    const handleDownloadDialogClose = useCallback(() => {
      setDownloadDialogState(CLOSED_DOWNLOAD_DIALOG_STATE);
    }, []);

    const handleEmailDialogClose = useCallback(() => {
      setIsEmailDialogOpen(false);
    }, []);

    const handleEmailSend = useCallback(
      async (emails: OutboundEmailEntry[]) => {
        const { operationId, recipientCount } = await sendSettlementEmail({
          companyId,
          emails,
        });

        enqueueOutboundEmail(operationId, recipientCount);
      },
      [companyId, enqueueOutboundEmail, sendSettlementEmail],
    );

    const handleStartSettlementExport = useCallback(
      async (companyExportRenderer: CompanyExportRenderer) => {
        if (isSubmittingExportRef.current) {
          return;
        }

        isSubmittingExportRef.current = true;
        setIsSubmittingExport(true);

        try {
          const hasStartedExport = await startSettlementExport({
            companyId,
            rendererKey: companyExportRenderer.rendererKey,
            settlementIds: selectedSettlementIds,
          });

          if (hasStartedExport) {
            setRendererPendingConfirmation(null);
            onClearSelection();
          }
        } finally {
          isSubmittingExportRef.current = false;
          setIsSubmittingExport(false);
        }
      },
      [companyId, onClearSelection, selectedSettlementIds, startSettlementExport],
    );

    const handleRendererSelect = useCallback(
      (companyExportRenderer: CompanyExportRenderer) => {
        const selectionIncludesExportedSettlements =
          alreadyExportedSettlementIds.length > 0;

        if (selectionIncludesExportedSettlements) {
          setRendererPendingConfirmation(companyExportRenderer);
          return;
        }

        void handleStartSettlementExport(companyExportRenderer);
      },
      [alreadyExportedSettlementIds.length, handleStartSettlementExport],
    );

    const handleReExportConfirm = useCallback(() => {
      if (rendererPendingConfirmation) {
        void handleStartSettlementExport(rendererPendingConfirmation);
      }
    }, [handleStartSettlementExport, rendererPendingConfirmation]);

    const handleReExportDialogClose = useCallback(() => {
      setRendererPendingConfirmation(null);
    }, []);

    const reportBulkExportResult = useCallback(
      (
        result: BulkSettlementExportResult | null,
        settlementLabelsById: Map<string, string>,
      ) => {
        if (result === null) {
          toasterStore.push(
            alert(
              t('approvals.driver_pay.settlements.bulk_export_to_accounting.timed_out'),
              AlertTypes.info,
            ),
          );
          return;
        }

        const queuedCount = result.queuedSettlementIds.length;
        const skippedNote =
          result.skippedCount > 0
            ? t('approvals.driver_pay.settlements.bulk_export_to_accounting.skipped', {
                count: result.skippedCount,
              })
            : null;

        if (result.failures.length > 0) {
          const failureRows = result.failures.map((failure) => ({
            message: failure.message,
            settlementId: failure.settlementId,
            settlementLabel:
              settlementLabelsById.get(failure.settlementId) ?? failure.settlementId,
          }));

          toasterStore.push(
            alert(
              <BulkExportSettlementsFailedToast
                failures={failureRows}
                skippedCount={result.skippedCount}
              />,
              queuedCount > 0 ? AlertTypes.warn : AlertTypes.error,
              null,
            ),
            true,
          );
          return;
        }

        const exportedNote =
          queuedCount > 0
            ? t('approvals.driver_pay.settlements.bulk_export_to_accounting.success', {
                count: queuedCount,
              })
            : t(
                'approvals.driver_pay.settlements.bulk_export_to_accounting.nothing_exported',
              );

        toasterStore.push(
          alert(
            [exportedNote, skippedNote].filter(Boolean).join(' '),
            queuedCount > 0 ? AlertTypes.success : AlertTypes.info,
          ),
        );
      },
      [toasterStore],
    );

    const handleBulkExportClick = useCallback(async () => {
      setIsVerifyingAccountingConnection(true);

      try {
        const isConnectionStillSynchronized = await onVerifyAccountingConnection();

        if (isConnectionStillSynchronized) {
          setIsBulkExportDialogOpen(true);
        } else {
          toasterStore.push(
            alert(
              t(
                'approvals.driver_pay.settlements.bulk_export_to_accounting.not_connected',
              ),
              AlertTypes.error,
            ),
          );
        }
      } finally {
        setIsVerifyingAccountingConnection(false);
      }
    }, [onVerifyAccountingConnection, toasterStore]);

    const handleConfirmBulkExport = useCallback(async () => {
      if (isBulkExportInFlightRef.current) {
        return;
      }

      isBulkExportInFlightRef.current = true;
      updateIsSubmittingBulkExport(true);

      const settlementLabelsById = new Map<string, string>();
      selectedSettlementIds.forEach((settlementId, selectedIndex) => {
        const settlement = selectedSettlements[selectedIndex];

        if (settlement) {
          settlementLabelsById.set(
            settlementId,
            getFriendlySettlementId(settlement, category),
          );
        }
      });
      const resultTracker = trackBulkExportSettlementsResult(
        userStore.user?.id ?? '',
        exportableSettlementIds,
      );

      try {
        await resultTracker.subscribed;
        await bulkExportSettlements(companyId, exportableSettlementIds);
      } catch {
        // bulkExportSettlements has already toasted and reported the rejection.
        resultTracker.cancel();
        isBulkExportInFlightRef.current = false;
        updateIsSubmittingBulkExport(false);
        return;
      }

      setIsBulkExportDialogOpen(false);
      updateIsSubmittingBulkExport(false);
      setIsAwaitingBulkExportResult(true);

      try {
        const result = await resultTracker.result;
        // Failed settlements stay checked so they can be retried; with no result the
        // outcome is unknown, so the whole submission is released.
        const failedSettlementIds = new Set(
          result?.failures.map((failure) => failure.settlementId),
        );

        onDeselectSettlements(
          exportableSettlementIds.filter((id) => !failedSettlementIds.has(id)),
        );
        reportBulkExportResult(result, settlementLabelsById);
      } finally {
        isBulkExportInFlightRef.current = false;
        setIsAwaitingBulkExportResult(false);
      }
    }, [
      bulkExportSettlements,
      category,
      companyId,
      exportableSettlementIds,
      onDeselectSettlements,
      reportBulkExportResult,
      selectedSettlementIds,
      selectedSettlements,
      trackBulkExportSettlementsResult,
      updateIsSubmittingBulkExport,
      userStore.user?.id,
    ]);

    const handleBulkExportDialogClose = useCallback(() => {
      setIsBulkExportDialogOpen(false);
    }, []);

    const selectedCount = selectedSettlementIds.length;
    const isVisible = selectedCount > 0;

    return (
      <>
        {isVisible && (
          <BulkActionsBarComponent>
            <BulkActionsBarCountButton onClick={onClearSelection}>
              {t('approvals.driver_pay.settlements.bulk_count_selected', {
                count: selectedCount,
                plural: selectedCount > 1 ? 's' : '',
              })}
            </BulkActionsBarCountButton>

            <BulkActionsBarButton
              color="inherit"
              loadingPosition="start"
              onClick={handleDownloadFilesClick}
              startIcon={<Download />}
              loading={downloadDialogState.isLoading}
            >
              {t('approvals.driver_pay.settlements.download_files')}
            </BulkActionsBarButton>

            {isSelectionEmailable && (
              <BulkActionsBarButton
                color="inherit"
                onClick={() => setIsEmailDialogOpen(true)}
                startIcon={<Email />}
              >
                {t('approvals.driver_pay.settlements.email_settlement.email_cta')}
              </BulkActionsBarButton>
            )}

            {isSelectionExportable && (
              <SettlementExportButton
                companyExportRenderers={companyExportRenderersForSide}
                isExporting={isSubmittingExport}
                onRendererSelect={handleRendererSelect}
              />
            )}

            {isAccountingExportEnabled && (
              <BulkActionsBarButton
                color="inherit"
                data-test-id="settlement-bulk-export-to-accounting-button"
                disabled={exportableSettlementIds.length === 0}
                loading={
                  isSubmittingBulkExport ||
                  isAwaitingBulkExportResult ||
                  isVerifyingAccountingConnection
                }
                loadingPosition="start"
                onClick={handleBulkExportClick}
                startIcon={<CloudUpload />}
              >
                {t('approvals.driver_pay.settlements.bulk_export_to_accounting.button')}
              </BulkActionsBarButton>
            )}
          </BulkActionsBarComponent>
        )}

        <EmailSettlementsDialog
          companyId={companyId}
          isOpen={isEmailDialogOpen}
          onClose={handleEmailDialogClose}
          onSend={handleEmailSend}
          settlementIds={selectedSettlementIds}
        />

        <DownloadFilesDialog
          isOpen={downloadDialogState.isOpen}
          jobIds={downloadDialogState.jobIds}
          onClose={handleDownloadDialogClose}
          preselectTimesheet={downloadDialogState.preselectTimesheet}
        />

        <SettlementReExportConfirmationDialog
          alreadyExportedSettlementIds={alreadyExportedSettlementIds}
          isOpen={Boolean(rendererPendingConfirmation)}
          isSubmitting={isSubmittingExport}
          onClose={handleReExportDialogClose}
          onConfirm={handleReExportConfirm}
          rendererLabel={
            rendererPendingConfirmation
              ? getSettlementExportRendererLabel(rendererPendingConfirmation.rendererKey)
              : ''
          }
        />

        <BulkExportSettlementsConfirmationDialog
          category={category}
          excludedCount={excludedFromBulkExportCount}
          exportableSettlements={exportableSettlements}
          isOpen={isBulkExportDialogOpen}
          maxCount={MAX_BULK_EXPORT_SETTLEMENTS}
          isSubmitting={isSubmittingBulkExport}
          onClose={handleBulkExportDialogClose}
          onConfirm={handleConfirmBulkExport}
        />
      </>
    );
  },
);

export default SettlementsBulkActionsBar;
